import { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import DOMPurify from 'dompurify';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import TeamService from '@Helpers/URLHelpers/TeamService';
import OrgService from '@Helpers/URLHelpers/OrgService';
import ErrorHandler from '@Root/ErrorHandler';
import { BsInfoCircle as Info } from 'react-icons/bs';
import { IoClose as Close } from 'react-icons/io5';
import { HiUserGroup } from 'react-icons/hi';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import './CreateTeamForm.scss';

export default function CreateTeamForm(props) {

    const [teamName, setTeamName] = useState('');
    const [cursor, setCursor] = useState('auto');
    const [teamTag, setTeamTag] = useState('');
    const [Teams, setTeams] = useState([]);
    const [isValidTeam, setIsValidTeam] = useState(true);
    const [isValidTag, setIsValidTag] = useState(true);
    const [edited, setEdited] = useState(false);
    const [anchorTagPopover, setAnchorTagPopover] = useState(null);
    const [visibility, setVisibility] = useState({value: 'private', label: 'Private', definition: 'Your Team will not show up in the search bar for non-members. Disabled if the parent is an Organization.'})
    const [visibilityDisabled, setVisibilityDisabled] = useState(false);
    const [parentOptions, setParentOptions] = useState([]);
    const [parent, setParent] = useState();
    const userAuthService = new UserAuthService();
    const teamService = new TeamService();
    const orgService = new OrgService();
    const errorHandler = new ErrorHandler();
    const { activeUserId, setActiveUserPermission, setActiveUserAccessList } = useReduxActiveUser();
    const className = 'createTeamForm';

    const visibilityOptions = [
        {value: 'public', label: 'Public', definition: 'This will allow other users to search for your Team, but will not allow them to see its contents.'},
        {value: 'private', label: 'Private', definition: 'Your Team will not show up in the search bar for non-members. Disabled if the parent is an Organization.'}
    ];

    useEffect(() => {
        renderData();
    }, []);

    const renderData = async() =>{
        setCursor('wait');
        try {
            let userOrgs = await orgService.getOrgListForUser(activeUserId);
            userOrgs = JSON.parse(userOrgs.body);
            let option = 2;
            let tempParentOptions = parentOptions;
            for(var i = 0; i < userOrgs.length; i++){
                let parentOrg = {
                    'option' : option,
                    'type': 'org',
                    'id': userOrgs[i].id,
                    'tag': userOrgs[i].tag,
                    'name': userOrgs[i].orgName
                }
                tempParentOptions = [...tempParentOptions, parentOrg];
                option++;
                if(userOrgs[i].id === JSON.parse(props.orgId)){
                    handleParentChange(null, parentOrg);
                    setParent(parentOrg);
                }
            }
            setParentOptions(tempParentOptions);
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const handleTeamNameChange = (e) => {
        let value = DOMPurify.sanitize(e.target.value);
        const validator = /^[a-zA-Z0-9][a-zA-Z0-9 \-_\.\&]*$/;
        if(value !== null && (validator.test(value) || value === '')) {
            setTeamName(value);
            setIsValidTeam(true);
            setEdited(value === null || value.trim() === '' ? false : true)
        }
    }

    const handleTeamTagChange = async(e) => {
        try {
            let value = DOMPurify.sanitize(e.target.value);
            const validator = /^[A-Z]{0,10}$/;
            const upperValue = value.toUpperCase();
            if(validator.test(upperValue) && value !== null) {
                setTeamTag(upperValue);
                setIsValidTeam(true);
                setIsValidTag(true);
                setEdited(value === null || value.trim() === '' ? false : true);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
    }

    const handleTagPopoverOpen = (event) => {
        setAnchorTagPopover(event.currentTarget);
    };

    const handleTagPopoverClose = () => {
        setAnchorTagPopover(null);
    };

    const handleSubmit = async(e) => {
        e?.preventDefault();
        setCursor('wait');
        try{
            e?.preventDefault();
            if(validateTag(teamTag)) {
                if(validateTeam(teamName, teamTag)) {
                    let team = {
                        teamName: teamName,
                        tag: teamTag,
                        orgId: props.orgId,
                        creatorUserId: activeUserId,
                        ownerUserId: activeUserId,
                        parentType: parent.type,
                        parentId: parent.id,
                        visibility: visibility.value,
                    };
                    await teamService.createTeam(team);
                    let updatedPermissions = await userAuthService.getUserPermissions(activeUserId);
                    updatedPermissions = JSON.parse(updatedPermissions.body);
                    setActiveUserPermission(updatedPermissions);
                    props.reloadData();
                    props.closePopup();
                } else {
                    setIsValidTeam(false);
                }
            } else {
                setIsValidTag(false);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const validateTag = (tag) => {
        const validator = /^[A-Z]{3,10}$/;
        return validator.test(tag);
    }

    const validateTeam = (name, _tag) => {
        let teams = Teams;
        if(teams === null || teams.length === 0) return true;
        for(let i = 0; i < teams.length; i++) {
            if(teams[i].teamName === name.trim())
                return false;
        }
        return true;
    }

    const handleParentChange = (event, value) => {
        setVisibility(visibilityOptions[0]);
        setVisibilityDisabled(true);
        setParent(value)
    }

    const handleChange = (selectedOption) => {
        setVisibility(selectedOption);
    };

    const formatMenuOptionLabel = (option) => (
        <div className={className + '__visibility__select'}>
            <div className={className + '__visibility__select__t1'}>{option.label}</div>
            <div className={className + '__visibility__select__t1'}>{option.definition}</div>
         </div>
    );

    const formatValueOptionLabel = (option) => (
        <div className={className + '__visibility__select'}>
            <div className={className + '__visibility__select__t1'}>{option.label}</div>
         </div>
    );

    return (
        <div className={className} style={{ cursor: cursor }}>
            <div className={`${className}__container`}>
                {/* Header */}
                <div className={`${className}__header`}>
                    <div className={`${className}__header__left`}>
                        <HiUserGroup className={`${className}__header__icon`} />
                        <div>
                            <h2 className={`${className}__header__title`}>Create Team</h2>
                            <p className={`${className}__header__subtitle`}>Set up a new team for your organization</p>
                        </div>
                    </div>
                    <button 
                        className={`${className}__header__close`}
                        onClick={props.closePopup}
                        disabled={cursor === 'wait'}
                        aria-label="Close"
                    >
                        <Close />
                    </button>
                </div>

                {/* Content */}
                <form onSubmit={handleSubmit}>
                    <div className={`${className}__content`}>
                        {/* Parent Organization Display */}
                        <div className={`${className}__parent`}>
                            <label className={`${className}__parent__label`}>Parent Organization</label>
                            <div className={`${className}__parent__display`}>
                                <span className={`${className}__parent__tag`}>{parent?.tag}</span>
                                <span className={`${className}__parent__name`}>{parent?.name}</span>
                            </div>
                        </div>

                        {/* Team Name and Tag Fields */}
                        <div className={`${className}__fields`}>
                            <div className={`${className}__field`}>
                                <label>
                                    Team Name
                                </label>
                                <TextField
                                    value={teamName}
                                    placeholder='Enter team name...'
                                    variant='outlined'
                                    disabled={cursor === 'wait'}
                                    onChange={handleTeamNameChange}
                                    required
                                />
                                {!isValidTeam && (
                                    <span className={`${className}__field__error`}>
                                        Team name already exists!
                                    </span>
                                )}
                            </div>

                            <div className={`${className}__field`}>
                                <label>
                                    Team Tag
                                    <Tooltip
                                        title={
                                            <div className={`${className}__info`}>
                                                <p className={`${className}__info__title`}>Tag Requirements:</p>
                                                <p>• Up to 10 uppercase letters only</p>
                                                <p className="blue-text">• Example: ABC, TSTO, POJIC</p>
                                                <p className="red-text">• No special characters or numbers</p>
                                                <p>• Used to mark items in this team</p>
                                            </div>
                                        }
                                        arrow
                                        placement="right"
                                    >
                                        <Info className={`${className}__field__info`} />
                                    </Tooltip>
                                </label>
                                <TextField
                                    value={teamTag}
                                    placeholder='Enter team tag...'
                                    variant='outlined'
                                    disabled={cursor === 'wait'}
                                    onChange={handleTeamTagChange}
                                    required
                                />
                                {!isValidTag && (
                                    <span className={`${className}__field__error`}>
                                        Invalid tag format!
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className={`${className}__footer`}>
                        <button
                            type="button"
                            className={`${className}__button secondary`}
                            onClick={props.closePopup}
                            disabled={cursor === 'wait'}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`${className}__button primary`}
                            disabled={teamTag.length < 3 || cursor === 'wait'}
                        >
                            Create Team
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
